import React from 'react';
import I18n from '@/modules/i18n';
import Icon from '@/components/ui/Icon';
import ReactSelect5, { Option } from '@/components/common/ReactSelect5';
import { capitalizeWords } from '@/modules/TCIUtils';
import { CompletionStatusFilter, GradingStatus } from '@/components/staffer/AssignmentsBasedGradesTable/types';
import Spinner from '@/components/common/Modal/Spinner';
import useGradesTableStore from '../store/useGradesTableStore';
import useStudentSubmissions from '../hooks/useStudentSubmissions';

interface CompletionStatusOption {
  label: string;
  value: CompletionStatusFilter;
}

interface GradingStatusOption {
  label: string;
  value: GradingStatus;
}

const completionStatusOptions: CompletionStatusOption[] = [
  {
    label: 'Turned In',
    value: 'turned-in',
  },
  {
    label: 'In Progress',
    value: 'in-progress',
  },
  {
    label: 'Low Score',
    value: 'low-score',
  },
];

const gradingStatusOptions: GradingStatusOption[] = [
  {
    label: I18n.t('graded'),
    value: 'graded',
  },
  {
    label: capitalizeWords(I18n.t('needs_grading')),
    value: 'needs_grading',
  },
  {
    label: I18n.t('partially_graded'),
    value: 'partially_graded',
  },
];

const FiltersPopup = ({ closeModal }) => {
  const filters = useGradesTableStore(state => state.filters);
  const setFilter = useGradesTableStore(state => state.setFilter);

  // Fetch submissions data for filtering purposes
  const { isFetching } = useStudentSubmissions({ forFilterData: true });

  // For QA- this is behind a feature flag and can be removed once functionality is added
  console.log('filters: ', filters);

  const completionStatusValue = completionStatusOptions.filter((o: CompletionStatusOption) => (
    filters.completionStatus.includes(o.value)
  ));

  const gradingStatusValue = gradingStatusOptions.filter((o: GradingStatusOption) => (
    filters.gradingStatus.includes(o.value)
  ));

  const updateFilters = (opt: Option[], filterName) => {
    setFilter({ [filterName]: opt.map(o => o.value) });
  };

  const resetFilters = () => setFilter({ completionStatus: [], gradingStatus: [] });

  const Header = () => (
    <div className={`tw-flex tw-px-5 tw-py-2 tw-items-center
                     tw-justify-between tw-rounded-t-[4px] tw-text-base
                     tw-border-b-[.5px] tw-border-solid tw-border-light-grey`}
    >
      <div className="tw-flex tw-items-center">
        <Icon
          fill="#777770"
          icon="Filter"
        />
        <div className="tw-text-lg tw-font-bold tw-pl-3">
          {I18n.t('filters')}
        </div>
      </div>
      <div className="tw-flex tw-items-center tw-text-lg">
        <Icon
          className="tw-cursor-pointer"
          fill="#522E91"
          icon="Close"
          size={32}
          onClick={closeModal}
        />
      </div>
    </div>
  );

  const FilterContent = () => (
    <div className="tw-px-4 tw-pb-4 tw-pt-2">
      <button
        className="tw-float-right tw-text-tci-purple tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer"
        type="button"
        onClick={resetFilters}
      >
        {I18n.t('clear_all_filters')}
      </button>

      <div className="tw-mb-5">
        <label
          aria-label="Grading Status"
          className="tw-w-full"
          htmlFor="grading-status-toggle"
        >
          <span className="tw-font-bold tw-block tw-mb-1 tw-ml-1">
            {I18n.t('grading_status')}
          </span>
          <ReactSelect5
            isMulti
            aria-label="dropdown-toggle"
            className="tw-w-full"
            id="grading-status-toggle"
            options={gradingStatusOptions}
            value={gradingStatusValue}
            onChange={(opt: Option[]) => updateFilters(opt, 'gradingStatus')}
          />
        </label>
      </div>

      <div className="tw-mb-2">
        <label
          aria-label="Status"
          className="tw-w-full"
          htmlFor="completion-status-toggle"
        >
          <span className="tw-font-bold tw-block tw-mb-1 tw-ml-1">
            {I18n.t('status')}
          </span>
          <ReactSelect5
            isMulti
            aria-label="dropdown-toggle"
            className="tw-w-full"
            id="completion-status-toggle"
            options={completionStatusOptions}
            value={completionStatusValue}
            onChange={(opt: Option[]) => updateFilters(opt, 'completionStatus')}
          />
        </label>
      </div>

      <button
        className="btn btn--purple tw-float-right tw-cursor-pointer tw-my-5"
        type="button"
        onClick={closeModal}
      >
        {I18n.t('apply')}
      </button>
    </div>
  );

  return (
    <div className={`tw-absolute tw-right-4 tw-top-full tw-mt-3 tw-w-[350px] tw-bg-white tw-rounded-[4px]
                     tw-z-[100] tw-shadow-[0_6px_7px_0_rgba(0,0,0,0.15)]
                     tw-border tw-border-solid tw-rounded-b-[4px] tw-border-light-grey`}
    >
      <Header />

      {isFetching
        ? (
          <div className="tw-py-5">
            <Spinner isLoading />
          </div>
        )
        : <FilterContent />
      }
    </div>
  );
};

export default FiltersPopup;
